import {useState} from "react";
import {submitContact, submitSpecialOrder, type FieldRule} from "@corporatesclub/shared";
import {DsButton, DsField, DsInput} from "@corporatesclub/ui";
import {useFormValidation} from "@/lib/useFormValidation";
import {useI18n} from "./I18nProvider";
import {InfoPageHero} from "./InfoPageHero";

type ContactField = "nume" | "email" | "telefon" | "mesaj";
type SpecialField = "nume" | "email" | "telefon" | "produs" | "cod";

const contactRules: FieldRule<ContactField>[] = [
  {field: "nume", required: true},
  {field: "email", type: "email", required: true},
  {field: "telefon", required: true},
  {field: "mesaj", required: true}
];

const specialRules: FieldRule<SpecialField>[] = [
  {field: "nume", required: true},
  {field: "email", type: "email", required: true},
  {field: "telefon", required: true},
  {field: "produs", required: true},
  {field: "cod", required: true}
];

export function ContactView() {
  const {t} = useI18n();
  const [form, setForm] = useState({nume: "", email: "", telefon: "", mesaj: ""});
  const [special, setSpecial] = useState({nume: "", email: "", telefon: "", produs: "", cod: "", mesaj: ""});
  const [sent, setSent] = useState(false);
  const [specialSent, setSpecialSent] = useState(false);
  const [showSpecial, setShowSpecial] = useState(false);
  const [error, setError] = useState("");
  const [specialErr, setSpecialErr] = useState("");
  const {errors, clearError, validate} = useFormValidation<ContactField>();
  const {errors: specialErrors, clearError: clearSpecialError, validate: validateSpecial} = useFormValidation<SpecialField>();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if(!validate(contactRules, form, t)) return;
    try {
      await submitContact(form);
      setSent(true);
      setForm({nume: "", email: "", telefon: "", mesaj: ""});
    } catch(err) {
      setError(err instanceof Error ? err.message : t("Send failed"));
    }
  }
  async function onSpecialSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSpecialErr("");
    if(!validateSpecial(specialRules, special, t)) return;
    try {
      await submitSpecialOrder(special);
      setSpecialSent(true);
      setSpecial({nume: "", email: "", telefon: "", produs: "", cod: "", mesaj: ""});
    } catch(err) {
      setSpecialErr(err instanceof Error ? err.message : t("Send failed"));
    }
  }
  return (
    <div className="cc-infoPage cc-contact">
      <InfoPageHero title={t("Contact us")} />
      <div className="cc-infoPage__container">
      <div className="cc-contact__layout">
        <aside className="cc-contact__aside">
          <div className="cc-contact__card cc-contact__card--company">
            <h2 className="cc-contact__companyName">SC CORPORATE ANSWER SRL</h2>
            <dl className="cc-contact__legal">
              <div className="cc-contact__legalRow">
                <dt>CUI</dt>
                <dd>RO43489530</dd>
              </div>
              <div className="cc-contact__legalRow">
                <dt>NR. REG. COM.</dt>
                <dd>J40/17817/2020</dd>
              </div>
            </dl>
          </div>

          <div className="cc-contact__card">
            <ul className="cc-contact__infoList">
              <li className="cc-contact__infoItem">
                <span className="cc-contact__infoIcon" aria-hidden="true">
                  <span className="material-symbols-rounded">location_on</span>
                </span>
                <div className="cc-contact__infoBody">
                  <span className="cc-contact__infoLabel">{t("Address")}</span>
                  <p className="cc-contact__infoValue">Str. Daniel Danielopolu 34 E, Sector 1, {t("Bucharest")}</p>
                </div>
              </li>
              <li className="cc-contact__infoItem">
                <span className="cc-contact__infoIcon" aria-hidden="true">
                  <span className="material-symbols-rounded">account_balance</span>
                </span>
                <div className="cc-contact__infoBody">
                  <span className="cc-contact__infoLabel">{t("Bank")}</span>
                  <p className="cc-contact__infoValue">Bank Transilvania</p>
                </div>
              </li>
              <li className="cc-contact__infoItem">
                <span className="cc-contact__infoIcon" aria-hidden="true">
                  <span className="material-symbols-rounded">schedule</span>
                </span>
                <div className="cc-contact__infoBody">
                  <span className="cc-contact__infoLabel">{t("Work program")}</span>
                  <p className="cc-contact__infoValue">{t("Monday-Friday, between 9.00 and 17.30 (except for public holidays)")}</p>
                </div>
              </li>
              <li className="cc-contact__infoItem">
                <span className="cc-contact__infoIcon" aria-hidden="true">
                  <span className="material-symbols-rounded">mail</span>
                </span>
                <div className="cc-contact__infoBody">
                  <span className="cc-contact__infoLabel">{t("E-mail address")}</span>
                  <p className="cc-contact__infoValue">
                    <a href="mailto:goodies@corporatesclub.com" className="cc-contact__infoLink anim">goodies@corporatesclub.com</a>
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </aside>

        <div className="cc-contact__main">
          <section className="cc-contact__card">
            <form className="cc-contact__fields" id="contactForm" onSubmit={onSubmit} noValidate>
              <DsInput type="text" name="nume" value={form.nume} onChange={(e) => { setForm({...form, nume: e.target.value}); clearError("nume"); }} placeholder={t("Name")} className="cc-contact__input" error={errors.nume} aria-required="true" />
              <div className="cc-contact__row2">
                <DsInput type="email" name="email" value={form.email} onChange={(e) => { setForm({...form, email: e.target.value}); clearError("email"); }} placeholder={t("E-mail address")} className="cc-contact__input" error={errors.email} aria-required="true" />
                <DsInput type="text" name="telefon" value={form.telefon} onChange={(e) => { setForm({...form, telefon: e.target.value}); clearError("telefon"); }} placeholder={t("Phone number")} className="cc-contact__input" error={errors.telefon} aria-required="true" />
              </div>
              <DsField error={errors.mesaj}>
                <textarea name="mesaj" value={form.mesaj} onChange={(e) => { setForm({...form, mesaj: e.target.value}); clearError("mesaj"); }} placeholder={t("Message")} className="cc-contact__textarea cc-ds-input" aria-required="true" />
              </DsField>
              {sent ? <div className="cc-contact__alert cc-contact__alert--success">{t("Message was sent!")}</div> : null}
              {error ? <div className="cc-contact__alert cc-contact__alert--error">{error}</div> : null}
              <div className="cc-contact__actions">
                <DsButton type="submit" variant="primary" size="md" className="cc-contact__submit">{t("Send")}</DsButton>
              </div>
            </form>
          </section>

          <section className="cc-contact__special">
            <button type="button" className="cc-contact__toggle anim" aria-expanded={showSpecial} onClick={() => setShowSpecial(!showSpecial)}>
              <span className="cc-contact__toggleText">
                <span className="cc-contact__toggleTitle">{t("Special order request")}</span>
              </span>
              <span className="cc-contact__toggleIcon" aria-hidden="true">
                <span className="material-symbols-rounded">expand_more</span>
              </span>
            </button>
            {showSpecial ? (
              <div className="cc-contact__specialBody">
                <form className="cc-contact__fields" id="notifyForm" onSubmit={onSpecialSubmit} noValidate>
                  <DsInput type="text" value={special.nume} onChange={(e) => { setSpecial({...special, nume: e.target.value}); clearSpecialError("nume"); }} placeholder={t("Name")} className="cc-contact__input" error={specialErrors.nume} aria-required="true" />
                  <div className="cc-contact__row2">
                    <DsInput type="email" value={special.email} onChange={(e) => { setSpecial({...special, email: e.target.value}); clearSpecialError("email"); }} placeholder={t("E-mail address")} className="cc-contact__input" error={specialErrors.email} aria-required="true" />
                    <DsInput type="text" value={special.telefon} onChange={(e) => { setSpecial({...special, telefon: e.target.value}); clearSpecialError("telefon"); }} placeholder={t("Phone number")} className="cc-contact__input" error={specialErrors.telefon} aria-required="true" />
                  </div>
                  <div className="cc-contact__row2">
                    <DsInput type="text" value={special.produs} onChange={(e) => { setSpecial({...special, produs: e.target.value}); clearSpecialError("produs"); }} placeholder={t("Product name")} className="cc-contact__input" error={specialErrors.produs} aria-required="true" />
                    <DsInput type="text" value={special.cod} onChange={(e) => { setSpecial({...special, cod: e.target.value}); clearSpecialError("cod"); }} placeholder={t("Product code")} className="cc-contact__input" error={specialErrors.cod} aria-required="true" />
                  </div>
                  <textarea value={special.mesaj} onChange={(e) => setSpecial({...special, mesaj: e.target.value})} placeholder={t("Message")} className="cc-contact__textarea cc-ds-input" />
                  {specialSent ? <div className="cc-contact__alert cc-contact__alert--success">{t("Request sent!")}</div> : null}
                  {specialErr ? <div className="cc-contact__alert cc-contact__alert--error">{specialErr}</div> : null}
                  <div className="cc-contact__actions">
                    <DsButton type="submit" variant="primary" size="md" className="cc-contact__submit">{t("Send")}</DsButton>
                  </div>
                </form>
              </div>
            ) : null}
          </section>
        </div>
      </div>
      </div>
    </div>
  );
}
